// ============================================================
//  src/pages/auth/RegisterPage.js — Matches PHP UI design
// ============================================================
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { register, checkRef } from '../../api/client';
import { getError } from '../../utils/helpers';
import './auth.css';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    fname: '', lname: '', email: '', phone: '', password: '',
    ref: params.get('ref') || '',
  });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [refName, setRefName]   = useState('');
  const [showPass, setShowPass] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (!form.ref) { setRefName(''); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await checkRef(form.ref);
        setRefName(data.success && data.data?.name ? `✅ Referred by ${data.data.name}` : '❌ Invalid code');
      } catch { setRefName(''); }
    }, 600);
    return () => clearTimeout(t);
  }, [form.ref]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fname.trim() || !form.lname.trim() || !form.email.trim() || !form.phone.trim() || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await register(form);
      if (data.success) {
        toast.success('Account created! Please verify your email.');
        navigate('/verify-email', { state: { email: form.email } });
      } else {
        setError(data.message || 'Registration failed.');
      }
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-dot-grid" />
        <div className="auth-ring auth-r1" /><div className="auth-ring auth-r2" /><div className="auth-ring auth-r3" />
        <div className="auth-left-content">
          <span className="auth-brand">
            <div className="auth-brand-mark">C</div>
            <span className="auth-brand-name">Cloverr</span>
          </span>
        </div>
        <div className="auth-left-main">
          <div className="auth-eyebrow"><div className="auth-eyebrow-dot" /><span>Join Today</span></div>
          <div className="auth-headline">Start Earning<br />in <em>Minutes</em></div>
          <div className="auth-sub">Create your free account and start completing tasks to earn money.</div>
          <div className="auth-features">
            <div className="auth-feat"><div className="auth-feat-icon"><i className="fas fa-gift" /></div><div className="auth-feat-text"><strong>Referral Bonuses</strong><span>Earn by inviting friends</span></div></div>
            <div className="auth-feat"><div className="auth-feat-icon"><i className="fas fa-bolt" /></div><div className="auth-feat-text"><strong>Instant Setup</strong><span>Start earning right away</span></div></div>
            <div className="auth-feat"><div className="auth-feat-icon"><i className="fas fa-money-bill-wave" /></div><div className="auth-feat-text"><strong>Fast Withdrawals</strong><span>Get paid to your bank</span></div></div>
          </div>
        </div>
        <div className="auth-left-footer"><div className="auth-ssl"><i className="fas fa-lock" /> 256-bit SSL encrypted</div></div>
      </div>

      <div className="auth-right">
        <div className="auth-shell">
          <div className="auth-hdr">
            <h2>Create account 🚀</h2>
            <p>Fill in your details to get started.</p>
          </div>

          {error && (
            <div className="auth-alert auth-alert-error">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
              <div className="auth-field" style={{ marginBottom: 0 }}>
                <label>First Name</label>
                <div className="auth-field-wrap">
                  <i className="fas fa-user fi" />
                  <input type="text" placeholder="John" value={form.fname} onChange={set('fname')} />
                </div>
              </div>
              <div className="auth-field" style={{ marginBottom: 0 }}>
                <label>Last Name</label>
                <div className="auth-field-wrap">
                  <i className="fas fa-user fi" />
                  <input type="text" placeholder="Doe" value={form.lname} onChange={set('lname')} />
                </div>
              </div>
            </div>

            <div className="auth-field">
              <label>Email Address</label>
              <div className="auth-field-wrap">
                <i className="fas fa-envelope fi" />
                <input type="email" placeholder="john@example.com" value={form.email} onChange={set('email')} autoComplete="email" />
              </div>
            </div>

            <div className="auth-field">
              <label>Phone Number</label>
              <div className="auth-field-wrap">
                <i className="fas fa-phone fi" />
                <input type="tel" placeholder="08012345678" value={form.phone} onChange={set('phone')} />
              </div>
            </div>

            <div className="auth-field">
              <label>Password</label>
              <div className="auth-field-wrap">
                <i className="fas fa-lock fi" />
                <input type={showPass ? 'text' : 'password'} placeholder="Min. 6 characters" value={form.password} onChange={set('password')} autoComplete="new-password" />
                <i className={`fas ${showPass ? 'fa-eye-slash' : 'fa-eye'} fi fi-right`} onClick={() => setShowPass(!showPass)} />
              </div>
            </div>

            <div className="auth-field">
              <label>Referral Code <span style={{ fontWeight: 500, textTransform: 'none', color: 'var(--text-light)' }}>(optional)</span></label>
              <div className="auth-field-wrap">
                <i className="fas fa-link fi" />
                <input type="text" placeholder="Enter referral code" value={form.ref} onChange={set('ref')} />
              </div>
              {refName && <div style={{ fontSize: 12, marginTop: 5, color: 'var(--text-mid)' }}>{refName}</div>}
            </div>

            <button type="submit" className="btn-auth" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Creating account…</>
                : <><i className="fas fa-user-plus" /> Create Account</>
              }
            </button>
          </form>

          <div className="auth-bottom">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
